/* eslint-disable no-console */
import { db } from '@/plugins/firebase'
import scheduleService from '@/services/scheduleService'

export default {
  setEditedStudent: async ({ state, commit }, payload) => {
    commit('SET_EDITED_STUDENT', payload)
  },

  // Teacher tab
  setEditedTeacher: async ({ state, commit }, payload) => {
    commit('SET_EDITED_TEACHER', payload)
  },
  getTeachers: async ({ state, commit }, { orderByField, orientation }) => {
    const teachers = []
    const teachersRef = await db.collection('teachers').orderBy(orderByField, orientation)
    let snapShot = null
    if (!state.lastTeacher) {
      snapShot = await teachersRef.limit(state.pageBreak).get()
    } else {
      snapShot = await teachersRef.startAfter(state.lastTeacher).limit(state.pageBreak).get()
    }

    if (!snapShot.empty) {
      commit('SET_LAST_TEACHER', snapShot.docs[snapShot.docs.length - 1])
    }
    // Crear un mapa para detectar duplicados por email
    const emailMap = {}

    snapShot.forEach(doc => {
      const teacher = doc.data()
      teacher.id = doc.id
      teacher.showPassword = false

      // Antes, si hasPassword era true pero el documento no traía contraseña, aquí se
      // inventaba la cadena 'temporal-password' y la tabla la mostraba como si fuera la real.
      // La contraseña ya no viaja en este documento: la pide la tabla al servidor, y solo
      // cuando un admin pulsa el ojo.

      // Verificar si ya existe un profesor con este email
      if (!emailMap[teacher.email]) {
        emailMap[teacher.email] = true
        teachers.push(teacher)
      }
    })
    commit('CONCAT_TEACHERS', teachers)
    commit('SET_EXIST_TEACHERS', !snapShot.empty)
  },
  addTeacher: async ({ state, commit }, payload) => {
    // Unique email validation
    const teacherRef = await db.collection('teachers').where('email', '==', payload.email).get()
    if (!teacherRef.empty) {
      const error = new Error('Ya existe un profesor con este email.')
      error.code = 'auth/email-already-in-use'
      throw error
    }

    // Usar una función HTTP personalizada para crear el usuario
    // Esta es la forma recomendada para crear usuarios sin afectar la sesión actual
    let newUserUid = null

    try {
      // Importar Firebase Auth
      const { auth } = await import('@/plugins/firebase')

      // Crear el usuario sin afectar la sesión principal del administrador
      // Guardamos el usuario actual antes de cualquier operación
      const currentUser = auth.currentUser

      // Crear el usuario directamente con Firebase Auth
      // Esto no afectará la sesión actual porque usaremos un enfoque diferente
      const userCredential = await auth.createUserWithEmailAndPassword(payload.email, payload.password)
      newUserUid = userCredential.user.uid
      // Importante: Restaurar inmediatamente la sesión del usuario actual (admin)
      // Esto evita que Firebase cambie la sesión al nuevo usuario
      if (currentUser) {
        // Forzar la restauración de la sesión anterior
        await auth.updateCurrentUser(currentUser)
      }

      console.log('Usuario creado correctamente sin afectar la sesión actual')
    } catch (authError) {
      // Si el error es EMAIL_EXISTS, es posible que el usuario haya sido eliminado recientemente
      if (authError.code === 'auth/email-already-in-use') {
        const error = new Error('Este email ya está en uso en Firebase Authentication. Si acabas de eliminar este usuario, espera unos minutos antes de volver a crearlo.')
        error.code = 'auth/email-already-in-use'
        throw error
      }
      console.error('Error al crear el usuario:', authError)
      throw authError
    }

    // Create teacher doc in Firestore with UID from auth
    const docRef = await db.collection('teachers').add({
      uid: newUserUid,
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      created_at: new Date(),
      role: 'teacher',
      // Guardar un indicador de que existe contraseña, pero no la contraseña real
      hasPassword: true
    })

    // Obtener los datos del documento recién creado
    const teacherObject = (await docRef.get()).data()
    teacherObject.showPassword = false
    teacherObject.id = docRef.id

    // El usuario de Firebase Auth ya se creó arriba con esta contraseña; esta llamada guarda
    // ademas la copia cifrada que el admin podrá consultar despues. Antes solo se dejaba en
    // memoria de Vuex, así que se perdía al recargar la página y ya no había forma de verla.
    await scheduleService.setTeacherPassword(docRef.id, payload.password)

    // Actualizar el estado de Vuex con el nuevo profesor
    commit('UNSHIFT_TEACHERS', teacherObject)

    // Limpiar duplicados después de agregar el nuevo profesor
    const emailMap = {}
    const uniqueTeachers = []

    state.teachers.forEach(teacher => {
      if (!emailMap[teacher.email]) {
        emailMap[teacher.email] = true
        uniqueTeachers.push(teacher)
      }
    })

    commit('SET_TEACHERS', uniqueTeachers)

    return docRef
  },
  editTeacher: async ({ state, commit }, payload) => {
    // Unique email validation
    const teacherRef = await db.collection('teachers')
      .where('email', '==', payload.email)
      .get()
    if (!teacherRef.empty) {
      teacherRef.forEach(doc => {
        if (doc.id !== payload.id) {
          const error = new Error('Ya existe un profesor con este email.')
          error.code = 'auth/email-already-in-use'
          throw error
        }
      })
    }

    // Update teacher doc
    const docRef = await db.collection('teachers').doc(payload.id)

    const currentTeacher = (await docRef.get()).data()

    // El correo va primero y por separado. Vive en tres sitios: Firebase Auth (el login), este
    // documento, y la copia dentro de cada clase (el invitado del evento de Calendar).
    //
    // Se llama SIEMPRE, no solo cuando el correo del formulario difiere del documento. Ese
    // atajo dejaba sin arreglo los correos que se cambiaron a mano en la base de datos antes
    // de que existiera la función: quedaron bien en el documento y viejos en Firebase Auth, y
    // como el formulario y el documento ya coincidían, el panel no hacía nada y el profesor
    // seguía sin poder entrar con su correo. Ahora el servidor compara con cada sitio y
    // corrige el que esté desfasado.
    await scheduleService.updateTeacherEmail(payload.id, payload.email)

    await docRef.update({
      firstName: payload.firstName,
      lastName: payload.lastName,
      email: payload.email,
      hasPassword: true // Indicador de que tiene contraseña
    })

    // Cambio de contraseña. Este bloque estaba VACÍO: tenía el comentario "se necesitaría una
    // Cloud Function" y nada más, así que escribir una contraseña nueva en el formulario no
    // cambiaba nada — ni en Firebase Auth ni en Firestore. El profesor seguía con la anterior.
    // Ahora sí se aplica, a través de la función que tiene el Admin SDK.
    if (payload.password) {
      await scheduleService.setTeacherPassword(docRef.id, payload.password)
    }

    // Obtener los datos del documento actualizado
    const teacherObject = (await docRef.get()).data()
    teacherObject.showPassword = false
    teacherObject.id = docRef.id

    // Actualizar el estado de Vuex con el profesor modificado
    commit('REPLACE_TEACHER_BY_ID', teacherObject)

    return docRef
  },
  deleteTeacher: async ({ commit }, payload) => {
    // Acepta el profesor suelto (uso antiguo) o { teacher, force }.
    const teacher = payload.teacher || payload
    const force = !!payload.force
    // Antes esto solo borraba el documento de Firestore: el bloque que debia eliminar la
    // cuenta de acceso estaba vacio, con el comentario "en produccion, esto deberia manejarse
    // con una Cloud Function". El profesor desaparecia de la lista pero seguia pudiendo
    // iniciar sesion, y su correo quedaba ocupado para siempre. Ahora se borra de verdad.
    const result = await scheduleService.deleteTeacherAccount(teacher.id, force)

    commit('REMOVE_TEACHER', teacher)

    return { success: true, message: 'Profesor eliminado correctamente.', authDeleted: result.authDeleted, classesDeleted: result.classesDeleted || [] }
  },

  resetTeachers: async ({ commit }) => {
    commit('SET_LAST_TEACHER', null)
    commit('SET_EXIST_TEACHERS', false)
    commit('SET_TEACHERS', [])
  },

  // Función para limpiar duplicados por email en el estado actual
  cleanDuplicateTeachers: async ({ state, commit }) => {
    // Crear un mapa para detectar duplicados por email
    const emailMap = {}
    const uniqueTeachers = []

    // Recorrer los profesores actuales y quedarnos solo con uno por email
    state.teachers.forEach(teacher => {
      if (!emailMap[teacher.email]) {
        emailMap[teacher.email] = true
        uniqueTeachers.push(teacher)
      }
    })

    // Actualizar el estado con los profesores únicos
    commit('SET_TEACHERS', uniqueTeachers)
    return uniqueTeachers
  },

  // Classes actions
  getAllClasses: async ({ commit }) => {
    const classes = []
    const allClassesRef = db.collection('classes').orderBy('created_at', 'desc')
    const snapShot = await allClassesRef.get()
    snapShot.forEach((doc) => {
      const classItem = doc.data()
      classItem.id = doc.id
      classes.push(classItem)
    })
    commit('SET_ALL_CLASSES', classes)
  },

  async addClass ({ commit }, payload) {
    const classObject = await scheduleService.addClass(payload)
    commit('UNSHIFT_CLASSES', classObject)
    return classObject
  },
  editClass: async ({ state, commit, dispatch }, payload) => {
    // eslint-disable-next-line no-console
    console.log('1. editClass action iniciada con payload:', JSON.stringify(payload))
    try {
      // eslint-disable-next-line no-console
      console.log('2. Llamando a scheduleService.editClass')
      const classObject = await scheduleService.editClass(payload.classId, payload.updatedClass)
      // eslint-disable-next-line no-console
      console.log('3. Respuesta de scheduleService.editClass:', classObject ? 'Objeto recibido' : 'null')

      if (!classObject) {
        throw new Error('No se pudo actualizar la clase')
      }

      // eslint-disable-next-line no-console
      console.log('4. Actualizando estado con commits')
      commit('SET_CLASS_SELECTED', classObject)
      commit('REPLACE_ALL_CLASS_BY_ID', classObject)

      // eslint-disable-next-line no-console
      console.log('5. Retornando respuesta exitosa')
      const response = { success: true, data: classObject }
      // eslint-disable-next-line no-console
      console.log('6. Objeto de respuesta:', response)
      return response
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('7. Error en editClass action:', error)
      const errorResponse = {
        success: false,
        error: error.message || 'Error al actualizar la clase'
      }
      // eslint-disable-next-line no-console
      console.log('8. Retornando respuesta de error:', errorResponse)
      return errorResponse
    }
  },
  setNewClass: async ({ commit }, payload) => {
    commit('SET_NEW_CLASS', payload)
  },
  addWeekToNewClass: async ({ commit }, payload) => {
    commit('ADD_WEEK_TO_NEW_CLASS', payload)
  },
  replaceClassWeekByIndex: async ({ commit }, { week, index }) => {
    commit('REPLACE_CLASS_WEEK_BY_INDEX', { week, index })
  },
  removeWeekFromNewClassByIndex: async ({ commit }, index) => {
    commit('REMOVE_CLASS_WEEK_BY_INDEX', index)
  },

  deleteClass: async ({ commit }, classItem) => {
    await db.collection('classes').doc(classItem.id).delete()
    commit('REMOVE_ALL_CLASS', classItem)
  },

  setClassSelected: async ({ commit }, classItem) => {
    commit('SET_CLASS_SELECTED', classItem)
  },

  // Actions for schedule tab
  addStudentToClass: async ({ state, commit }, payload) => {
    const studentNestedObject = Object.assign({}, state.classSelected.students)
    if (payload.guest) {
      // También crear/actualizar en la colección global de estudiantes para que aparezca en los listados
      const { student } = await scheduleService.updateOrCreateStudent({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        guest: true,
        flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true
      })

      // Usar el ID del estudiante como clave interna en la clase
      studentNestedObject[student.id] = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        guest: true,
        flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true,
        active: true,
        createdAt: new Date()
      }

      // Asociar el curso a este estudiante (assignedCoursesIds y subcolección myCourses)
      const studentRef = await db.collection('students').doc(student.id)
      const newAssignedCoursesIds = student.assignedCoursesIds ? [...student.assignedCoursesIds] : []
      if (!newAssignedCoursesIds.includes(state.classSelected.id)) {
        newAssignedCoursesIds.push(state.classSelected.id)
      }
      await studentRef.set({ assignedCoursesIds: newAssignedCoursesIds }, { merge: true })

      const newLandingCourse = {
        created_at: state.classSelected.created_at,
        day: state.classSelected.day,
        name: state.classSelected.name,
        startHour: state.classSelected.startHour,
        endHour: state.classSelected.endHour,
        teacher: state.classSelected.teacher,
        level: state.classSelected.level,
        startDate: state.classSelected.startDate,
        nextSessionDate: state.classSelected.nextSessionDate,
        week: state.classSelected.week,
        originTimezone: state.classSelected.originTimezone
      }
      await db.collection(`students/${student.id}/myCourses`).doc(state.classSelected.id).set(newLandingCourse, { merge: true })
    } else {
      const { student } = await scheduleService.updateOrCreateStudent({
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true
      })
      studentNestedObject[student.id] = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        email: payload.email,
        guest: payload.guest,
        flashcardsEnabled: payload.flashcardsEnabled !== undefined ? payload.flashcardsEnabled : true,
        active: true,
        createdAt: new Date()
      }

      const studentRef = await db.collection('students').doc(student.id)
      const newAssignedCoursesIds = student.assignedCoursesIds ? [...student.assignedCoursesIds] : []
      if (!newAssignedCoursesIds.includes(state.classSelected.id)) {
        newAssignedCoursesIds.push(state.classSelected.id)
      }
      await studentRef.set({ assignedCoursesIds: newAssignedCoursesIds }, { merge: true })
      // add new course to student courses subcollection
      const newLandingCourse = {
        created_at: state.classSelected.created_at,
        day: state.classSelected.day,
        name: state.classSelected.name,
        startHour: state.classSelected.startHour,
        endHour: state.classSelected.endHour,
        teacher: state.classSelected.teacher,
        level: state.classSelected.level,
        startDate: state.classSelected.startDate,
        nextSessionDate: state.classSelected.nextSessionDate,
        week: state.classSelected.week,
        originTimezone: state.classSelected.originTimezone
      }
      await db.collection(`students/${student.id}/myCourses`).doc(state.classSelected.id).set(newLandingCourse, { merge: true })
    }
    const updatedClass = await scheduleService.editClass(state.classSelected.id, {
      students: studentNestedObject,
      studentsIds: Object.keys(studentNestedObject)
    })

    commit('SET_CLASS_SELECTED', updatedClass)
    commit('REPLACE_ALL_CLASS_BY_ID', updatedClass)

    return updatedClass
  },

  deleteStudentFromClass: async ({ state, commit }, studentKey) => {
    const studentNestedObjectUpdated = Object.assign({}, state.classSelected.students)
    delete studentNestedObjectUpdated[studentKey]
    const updatedClass = await scheduleService.editClass(state.classSelected.id, {
      students: studentNestedObjectUpdated,
      studentsIds: Object.keys(studentNestedObjectUpdated)
    })
    commit('SET_CLASS_SELECTED', updatedClass)
    commit('REPLACE_ALL_CLASS_BY_ID', updatedClass)
    return updatedClass
  },

  //  Get all students
  fetchAllStudents: async ({ state, commit }, { orderByField, orientation }) => {
    const students = []
    const studentsRef = db.collection('students').orderBy(orderByField, orientation)
    let snapShot = null
    if (!state.lastStudent) {
      snapShot = await studentsRef.limit(state.pageBreak).get()
    } else {
      snapShot = await studentsRef.startAfter(state.lastStudent).limit(state.pageBreak).get()
    }

    if (!snapShot.empty) {
      commit('SET_LAST_STUDENT', snapShot.docs[snapShot.docs.length - 1])
    }
    snapShot.forEach(doc => {
      const student = doc.data()
      student.id = doc.id
      students.push(student)
    })
    commit('CONCAT_STUDENTS', students)
    commit('SET_EXIST_STUDENTS', !snapShot.empty)
  },

  resetAllStudents: async ({ commit }) => {
    commit('SET_LAST_STUDENT', null)
    commit('SET_EXIST_STUDENTS', false)
    commit('SET_ALL_STUDENTS', [])
  },
  // Nuevas acciones para lista general de estudiantes
  updateStudentStatus: async (_ctx, { studentId, status }) => {
    // status: 'active' | 'suspended_temp' | 'suspended_perm' | 'guest'
    const payload = { status }
    if (status === 'guest') {
      payload.guest = true
    }
    if (status !== 'guest') {
      payload.guest = false
    }
    // active flag coherente con estado
    if (status === 'active') payload.active = true
    if (status === 'suspended_temp' || status === 'suspended_perm') payload.active = false
    await scheduleService.updateStudent(studentId, payload)
  },
  appendStudentAssistance: async (_ctx, { studentId, value }) => {
    // value: boolean (true asistió, false faltó)
    const student = await scheduleService.getStudent(studentId)
    const assistance = Array.isArray(student.assistance) ? [...student.assistance] : []
    assistance.push({ value, date: new Date() })
    const assistanceCounter = assistance.reduce((acc, it) => acc + (it.value ? 1 : 0), 0)
    await scheduleService.updateStudent(studentId, { assistance, assistanceCounter })
  }
}
